import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container, Typography, Paper, Box, Grid, TextField, Button,
  FormControl, InputLabel, Select, MenuItem, FormHelperText,
  InputAdornment, Divider, Chip, IconButton, Alert, CircularProgress,
  List, ListItem, ListItemText, ListItemSecondaryAction, Autocomplete,
  Card, CardContent, CardActions
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  AccessTime as AccessTimeIcon,
  AttachMoney as AttachMoneyIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';

// Get API URL from environment variables
import { API_URL } from '../../utils/apiConfig';
import { getCurrencySymbol, formatCurrencySync } from '../../utils/currencyUtils';
import ToothSelector from '../../components/treatments/ToothSelector';
import { getApiErrorMessage } from '../../utils/errorUtils';
import { formatFailedActionMessage, formatSuccessActionMessage } from '../../utils/uiMessages';

const AddTreatment = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [loadingTreatments, setLoadingTreatments] = useState(false);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [loadingClinics, setLoadingClinics] = useState(false);
  const [error, setError] = useState(null);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [allTreatments, setAllTreatments] = useState([]); // All treatment definitions for procedure selection
  const [selectedProcedures, setSelectedProcedures] = useState([]); // Array of { treatment: TreatmentDefinition, teeth: [numbers], quantity: number, cost: number }
  const [currentProcedureTreatment, setCurrentProcedureTreatment] = useState(null); // Currently selected treatment for tooth selection
  const [toothSelectorOpen, setToothSelectorOpen] = useState(false);
  
  // Get pre-filled values from URL params
  const patientIdFromUrl = searchParams.get('patientId');
  const dateFromUrl = searchParams.get('date');
  const timeFromUrl = searchParams.get('time');
  const durationFromUrl = searchParams.get('duration');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    category: '',
    description: '',
    price: '',
    duration: durationFromUrl || '', // Duration from URL or empty
    requiredEquipment: [],
    notes: '',
    isActive: true,
    patient: patientIdFromUrl || null,
    user: null, // Doctor (user) – set default when doctors load
    clinic: null,
    treatmentDate: dateFromUrl || '',
    treatmentTime: timeFromUrl || ''
  });
  
  // Equipment input state
  const [newEquipment, setNewEquipment] = useState('');
  
  // Form validation errors
  const [errors, setErrors] = useState({});

  // Treatments sorted by category then name (for grouped Autocomplete)
  const treatmentsByCategory = useMemo(() => {
    const list = allTreatments || [];
    return [...list].sort((a, b) => {
      const catA = a.category || 'Other';
      const catB = b.category || 'Other';
      if (catA !== catB) return catA.localeCompare(catB);
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [allTreatments]);

  // Load patients on component mount
  useEffect(() => {
    const loadPatients = async () => {
      setLoadingPatients(true);
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('Authentication token not found');
        }

        const response = await axios.get(`${API_URL}/patients`, {
          headers: {
            Authorization: `Bearer ${token}`
          },
          params: {
            limit: 1000,
            status: 'active'
          }
        });
        
        setPatients(response.data.data || []);
      } catch (err) {
        console.error('Error loading patients:', err);
        toast.error(formatFailedActionMessage('load', 'patients'));
      } finally {
        setLoadingPatients(false);
      }
    };

    loadPatients();
  }, []);

  // Load doctors and clinics
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const loadDoctors = async () => {
      setLoadingDoctors(true);
      try {
        const res = await axios.get(`${API_URL}/users/doctors`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const list = res.data?.data || res.data || [];
        setDoctors(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error('Error loading doctors:', err);
      } finally {
        setLoadingDoctors(false);
      }
    };
    const loadClinics = async () => {
      setLoadingClinics(true);
      try {
        const res = await axios.get(`${API_URL}/clinics`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const list = res.data?.data || res.data || [];
        setClinics(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error('Error loading clinics:', err);
      } finally {
        setLoadingClinics(false);
      }
    };
    loadDoctors();
    loadClinics();
  }, []);

  // Default doctor to current user when doctors are loaded
  useEffect(() => {
    if (doctors.length > 0 && currentUser?._id && !formData.user) {
      const me = doctors.find((d) => d._id === currentUser._id);
      if (me) {
        setFormData((prev) => ({ ...prev, user: currentUser._id }));
      }
    }
  }, [doctors, currentUser?._id]);

  // Load all treatments for procedure selection
  useEffect(() => {
    const loadTreatments = async () => {
      setLoadingTreatments(true);
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('Authentication token not found');
        }

        const response = await axios.get(`${API_URL}/treatments`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        setAllTreatments(response.data.data || []);
      } catch (err) {
        console.error('Error loading treatments:', err);
        toast.error(formatFailedActionMessage('load', 'treatments'));
      } finally {
        setLoadingTreatments(false);
      }
    };

    loadTreatments();
  }, []);

  // Update patient selection when patientId from URL is available and patients are loaded
  useEffect(() => {
    if (patientIdFromUrl && patients.length > 0 && !formData.patient) {
      const patient = patients.find(p => p._id === patientIdFromUrl);
      if (patient) {
        setFormData(prev => ({
          ...prev,
          patient: patientIdFromUrl
        }));
      }
    }
  }, [patientIdFromUrl, patients, formData.patient]);

  // Handle procedure treatment selection - add to list
  const handleProcedureTreatmentChange = (event, newValue) => {
    if (newValue) {
      // Check if this treatment is already in the list
      const existingIndex = selectedProcedures.findIndex(
        p => (p.treatment._id || p.treatment.id) === (newValue._id || newValue.id)
      );
      
      if (existingIndex >= 0) {
        // If already exists, open tooth selector to modify it
        setCurrentProcedureTreatment(newValue);
        setToothSelectorOpen(true);
      } else {
        // New treatment - set it for tooth selection
        setCurrentProcedureTreatment(newValue);
        setToothSelectorOpen(true);
      }
    }
  };

  // Handle tooth selection confirmation
  const handleTeethSelected = (teeth) => {
    if (!currentProcedureTreatment) return;

    const quantity = teeth.length || 1; // If no teeth selected, count as 1
    const unitPrice = currentProcedureTreatment.price || 0;
    const totalPrice = unitPrice * quantity;
    const totalDuration = (currentProcedureTreatment.duration || 0) * quantity;

    // Check if this treatment already exists in the list
    const existingIndex = selectedProcedures.findIndex(
      p => (p.treatment._id || p.treatment.id) === (currentProcedureTreatment._id || currentProcedureTreatment.id)
    );

    const procedureData = {
      treatment: currentProcedureTreatment,
      teeth: teeth,
      quantity: quantity,
      cost: totalPrice,
      duration: totalDuration
    };

    let updatedProcedures;
    if (existingIndex >= 0) {
      // Update existing procedure
      updatedProcedures = [...selectedProcedures];
      updatedProcedures[existingIndex] = procedureData;
    } else {
      // Add new procedure
      updatedProcedures = [...selectedProcedures, procedureData];
    }

    setSelectedProcedures(updatedProcedures);

    // Auto-fill form fields from the first procedure (or combine if multiple)
    if (updatedProcedures.length > 0) {
      const firstProcedure = updatedProcedures[0];
      const totalCost = updatedProcedures.reduce((sum, p) => sum + p.cost, 0);
      const totalDurationSum = updatedProcedures.reduce((sum, p) => sum + p.duration, 0);

      setFormData(prev => ({
        ...prev,
        name: firstProcedure.treatment.name || prev.name,
        code: firstProcedure.treatment.code || prev.code,
        category: firstProcedure.treatment.category || prev.category,
        // Сохраняем пользовательское описание, если оно уже введено
        description: prev.description || firstProcedure.treatment.description || '',
        price: String(totalCost), // Total cost of all procedures
        duration: String(totalDurationSum) // Total duration of all procedures
      }));
    }

    setCurrentProcedureTreatment(null);
    setToothSelectorOpen(false);
  };

  // Handle removing a procedure from the list
  const handleRemoveProcedure = (index) => {
    const updatedProcedures = selectedProcedures.filter((_, i) => i !== index);
    setSelectedProcedures(updatedProcedures);

    // Update form data based on remaining procedures
    if (updatedProcedures.length > 0) {
      const firstProcedure = updatedProcedures[0];
      const totalCost = updatedProcedures.reduce((sum, p) => sum + p.cost, 0);
      const totalDurationSum = updatedProcedures.reduce((sum, p) => sum + p.duration, 0);

      setFormData(prev => ({
        ...prev,
        name: firstProcedure.treatment.name || prev.name,
        code: firstProcedure.treatment.code || prev.code,
        category: firstProcedure.treatment.category || prev.category,
        // Не затираем уже введённое описание
        description: prev.description || firstProcedure.treatment.description || '',
        price: String(totalCost),
        duration: String(totalDurationSum)
      }));
    } else {
      // Clear form if no procedures left
      setFormData(prev => ({
        ...prev,
        price: '',
        duration: prev.duration || ''
      }));
    }
  };

  // Handle editing a procedure (change teeth selection)
  const handleEditProcedure = (procedure) => {
    setCurrentProcedureTreatment(procedure.treatment);
    setToothSelectorOpen(true);
  };
  
  // Categories for dental treatments
  const categories = [
    'Diagnostic',
    'Preventive',
    'Restorative',
    'Endodontic',
    'Periodontic',
    'Prosthodontic',
    'Oral Surgery',
    'Orthodontic',
    'Cosmetic',
    'Pediatric'
  ];
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    // For number fields, keep as string for TextField compatibility
    const processedValue = (name === 'price' || name === 'duration') ? value : value;
    setFormData({
      ...formData,
      [name]: processedValue
    });
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };
  
  const handleAddEquipment = () => {
    if (newEquipment.trim() !== '') {
      if (!formData.requiredEquipment.includes(newEquipment.trim())) {
        setFormData({
          ...formData,
          requiredEquipment: [...formData.requiredEquipment, newEquipment.trim()]
        });
      }
      setNewEquipment('');
    }
  };
  
  const handleRemoveEquipment = (index) => {
    const updatedEquipment = [...formData.requiredEquipment];
    updatedEquipment.splice(index, 1);
    setFormData({
      ...formData,
      requiredEquipment: updatedEquipment
    });
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    // If procedures are selected, validation is different
    if (selectedProcedures.length > 0) {
      // Procedures are selected - no need to validate form fields as they will be auto-filled
      // Just ensure we have at least one procedure
      if (selectedProcedures.length === 0) {
        newErrors.procedures = 'At least one procedure is required';
      }
    } else {
      // No procedures selected - validate manual entry fields
      if (!formData.name.trim()) newErrors.name = 'Treatment name is required';
      if (!formData.code.trim()) newErrors.code = 'Treatment code is required';
      if (!formData.category) newErrors.category = 'Category is required';
      if (!formData.description.trim()) newErrors.description = 'Description is required';
      
      // Price is optional, but if provided, must be valid
      if (formData.price && (isNaN(formData.price) || Number(formData.price) < 0)) {
        newErrors.price = 'Price must be a positive number';
      }
      
      if (!formData.duration) {
        newErrors.duration = 'Duration is required';
      } else if (isNaN(formData.duration) || Number(formData.duration) <= 0) {
        newErrors.duration = 'Duration must be a positive number';
      }
      
      // Validate category is in the allowed list
      if (formData.category && !categories.includes(formData.category)) {
        newErrors.category = `Category is invalid. Allowed values: ${categories.join(', ')}`;
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }
      
      // Prepare data for API - convert strings to numbers where needed
      // Combine date and time into a single Date object for treatmentDate
      let treatmentDateTime = null;
      if (formData.treatmentDate && formData.treatmentTime) {
        const dateStr = formData.treatmentDate;
        const timeStr = formData.treatmentTime;
        treatmentDateTime = new Date(`${dateStr}T${timeStr}`);
      } else if (formData.treatmentDate) {
        // If only date is provided, use start of day
        treatmentDateTime = new Date(`${formData.treatmentDate}T00:00:00`);
      }
      
      // If procedures are selected, create a treatment definition for each procedure
      if (selectedProcedures.length > 0) {
        const promises = selectedProcedures.map(async (procedure, index) => {
          // Generate unique code that fits within 20 character limit
          const baseCode = procedure.treatment.code || 'TREAT';
          const timestamp = Date.now().toString().slice(-6); // Last 6 digits
          const randomStr = Math.random().toString(36).substr(2, 3).toUpperCase(); // 3 random chars
          // Combine: baseCode (max 10) + '-' + timestamp (6) + random (3) = max 20 chars
          const maxBaseLength = Math.max(1, 20 - 1 - 6 - 3); // Reserve space for '-' + timestamp + random
          const truncatedBase = baseCode.substring(0, maxBaseLength);
          const uniqueCode = `${truncatedBase}-${timestamp}${randomStr}`.substring(0, 20);
          
          const apiData = {
            name: procedure.treatment.name,
            code: uniqueCode,
            category: procedure.treatment.category,
            description: formData.description || procedure.treatment.description,
            price: procedure.cost,
            duration: procedure.duration,
            patient: formData.patient || undefined,
            user: formData.user || undefined,
            clinic: formData.clinic || undefined,
            treatmentDate: treatmentDateTime || undefined,
            treatmentTime: formData.treatmentTime || undefined,
            teeth: procedure.teeth.length > 0 ? procedure.teeth : undefined,
            requiredEquipment: procedure.treatment.requiredEquipment || [],
            notes: formData.notes || undefined,
            isActive: formData.isActive
          };

          // Remove undefined values
          Object.keys(apiData).forEach(key => {
            if (apiData[key] === undefined || apiData[key] === '') {
              delete apiData[key];
            }
          });

          if (!apiData.patient) {
            delete apiData.patient;
          }

          return axios.post(`${API_URL}/treatments`, apiData, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
        });

        await Promise.all(promises);
        toast.success(formatSuccessActionMessage('created', `${selectedProcedures.length} treatment(s)`));
        setLoading(false);
        navigate('/treatments');
      } else {
        // Original logic for single treatment (manual entry)
        // Convert price to number, handling both string and number types
        let priceValue = 0;
        if (formData.price !== undefined && formData.price !== null && formData.price !== '') {
          if (typeof formData.price === 'string') {
            priceValue = formData.price.trim() !== '' ? Number(formData.price) : 0;
          } else {
            priceValue = Number(formData.price) || 0;
          }
        }

        const apiData = {
          ...formData,
          price: priceValue,
          duration: Number(formData.duration),
          patient: formData.patient || undefined,
          user: formData.user || undefined,
          clinic: formData.clinic || undefined,
          treatmentDate: treatmentDateTime || undefined,
          treatmentTime: formData.treatmentTime || undefined,
        };
        
        // Remove undefined and empty values (but keep price as 0 if it was empty)
        Object.keys(apiData).forEach(key => {
          if (key !== 'price' && (apiData[key] === undefined || apiData[key] === '' || apiData[key] === null)) {
            delete apiData[key];
          }
        });
        
        // Remove patient field if it's null/empty
        if (!apiData.patient) {
          delete apiData.patient;
        }
        
        // Make API call to add treatment
        await axios.post(`${API_URL}/treatments`, apiData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        toast.success(formatSuccessActionMessage('added', 'Treatment'));
        setLoading(false);
        navigate('/treatments');
      }
    } catch (err) {
      console.error('Error adding treatment:', err);
      
      // Get specific error message from response
      const errorMessage = getApiErrorMessage(err, formatFailedActionMessage('add', 'treatment'));
      
      setError(errorMessage);
      setLoading(false);
    }
  };
  
  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h5" component="h1">
            Add New Treatment
          </Typography>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Procedure Section */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Procedure
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  id="procedure-treatment"
                  options={treatmentsByCategory}
                  groupBy={(option) => option.category || 'Other'}
                  getOptionLabel={(option) => {
                    if (!option) return '';
                    return `${option.name || ''} (${option.code || ''}) - ${formatCurrencySync(option.price || 0)}`.trim();
                  }}
                  value={null} // Always null to allow selecting the same treatment multiple times
                  onChange={handleProcedureTreatmentChange}
                  loading={loadingTreatments}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Add Treatment from All Treatments"
                      helperText="Select a treatment to add it to the procedure list and select teeth"
                      placeholder="Search for a treatment..."
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <div>
                        <Typography variant="body1">{option.name || 'Unknown Treatment'}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {option.code || 'No code'} • {formatCurrencySync(option.price || 0)} • {option.duration || 0} mins
                        </Typography>
                      </div>
                    </li>
                  )}
                  renderGroup={(params) => (
                    <li key={params.key}>
                      <ul style={{ padding: 0, margin: 0, listStyle: 'none' }}>
                        <li>
                          <Typography
                            component="span"
                            variant="subtitle2"
                            sx={{
                              display: 'block',
                              px: 1.5,
                              py: 1,
                              bgcolor: 'grey.100',
                              fontWeight: 600
                            }}
                          >
                            {params.group}
                          </Typography>
                        </li>
                        {params.children}
                      </ul>
                    </li>
                  )}
                  disabled={loading || loadingTreatments}
                />
              </Grid>

              {/* List of selected procedures */}
              {selectedProcedures.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, mb: 1 }}>
                    Selected Procedures ({selectedProcedures.length})
                  </Typography>
                  {selectedProcedures.map((procedure, index) => (
                    <Card key={index} sx={{ mb: 2, bgcolor: 'grey.50' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Box>
                            <Typography variant="body1" fontWeight="medium">
                              {procedure.treatment.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {procedure.treatment.code} • {procedure.treatment.category}
                            </Typography>
                          </Box>
                          <Chip
                            label={formatCurrencySync(procedure.cost)}
                            color="primary"
                            size="small"
                          />
                        </Box>
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Teeth:</strong> {procedure.teeth.length > 0 
                              ? procedure.teeth.sort((a, b) => a - b).join(', ') 
                              : 'Not specified'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Quantity:</strong> {procedure.quantity} • 
                            <strong> Unit Price:</strong> {formatCurrencySync(procedure.treatment.price || 0)} • 
                            <strong> Duration:</strong> {procedure.duration} mins
                          </Typography>
                        </Box>
                      </CardContent>
                      <CardActions>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleEditProcedure(procedure)}
                        >
                          Change Teeth
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => handleRemoveProcedure(index)}
                        >
                          Remove
                        </Button>
                      </CardActions>
                    </Card>
                  ))}
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                    <Typography variant="body1" fontWeight="medium">
                      Total: {formatCurrencySync(selectedProcedures.reduce((sum, p) => sum + p.cost, 0))} • 
                      Duration: {selectedProcedures.reduce((sum, p) => sum + p.duration, 0)} mins
                    </Typography>
                  </Box>
                </Grid>
              )}

              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Treatment Name *"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  error={!!errors.name}
                  helperText={errors.name}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  id="patient"
                  options={patients}
                  getOptionLabel={(option) => {
                    if (!option) return '';
                    return `${option.name || ''} (${option.phone || 'No phone'})`.trim();
                  }}
                  value={patients.find(p => p._id === formData.patient) || null}
                  onChange={(event, newValue) => {
                    setFormData({
                      ...formData,
                      patient: newValue ? newValue._id : null
                    });
                    if (errors.patient) {
                      setErrors({
                        ...errors,
                        patient: ''
                      });
                    }
                  }}
                  loading={loadingPatients}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Patient"
                      error={!!errors.patient}
                      helperText={errors.patient || "Optional: Link this treatment to a specific patient"}
                      placeholder="Search for a patient..."
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <div>
                        <Typography variant="body1">{option.name || 'Unknown Patient'}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {option.phone || 'No phone'} | {option.email || 'No email'}
                        </Typography>
                      </div>
                    </li>
                  )}
                  disabled={loading || loadingPatients}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Doctor</InputLabel>
                  <Select
                    name="user"
                    value={formData.user || ''}
                    onChange={(e) => setFormData({ ...formData, user: e.target.value || null })}
                    label="Doctor"
                    disabled={loadingDoctors}
                  >
                    <MenuItem value="">—</MenuItem>
                    {doctors.map((d) => (
                      <MenuItem key={d._id} value={d._id}>
                        {d.name || d.email}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Treating doctor</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Clinic</InputLabel>
                  <Select
                    name="clinic"
                    value={formData.clinic || ''}
                    onChange={(e) => setFormData({ ...formData, clinic: e.target.value || null })}
                    label="Clinic"
                    disabled={loadingClinics}
                  >
                    <MenuItem value="">—</MenuItem>
                    {clinics.map((c) => (
                      <MenuItem key={c._id} value={c._id}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Clinic where treatment is performed</FormHelperText>
                </FormControl>
              </Grid>

              {/* Date and Time fields - always shown */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Treatment Date"
                  name="treatmentDate"
                  type="date"
                  value={formData.treatmentDate || ''}
                  onChange={handleChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  helperText={formData.treatmentDate ? "Date from appointment" : "Date when treatment was performed"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Treatment Time"
                  name="treatmentTime"
                  type="time"
                  value={formData.treatmentTime || ''}
                  onChange={handleChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  helperText={formData.treatmentTime ? "Time from appointment" : "Time when treatment was performed"}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Treatment Code *"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  error={!!errors.code}
                  helperText={errors.code}
                  required
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!errors.category} required>
                  <InputLabel>Category *</InputLabel>
                  <Select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    label="Category *"
                  >
                    {categories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.category && <FormHelperText>{errors.category}</FormHelperText>}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="isActive"
                    value={formData.isActive}
                    onChange={handleChange}
                    label="Status"
                  >
                    <MenuItem value={true}>Active</MenuItem>
                    <MenuItem value={false}>Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description *"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  placeholder="Enter a detailed description of the treatment procedure..."
                  error={!!errors.description}
                  helperText={errors.description || 'Provide a detailed treatment description'}
                  required
                />
              </Grid>
              
              {/* Pricing and Duration */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Pricing and Duration
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={`Price${getCurrencySymbol() ? ` (${getCurrencySymbol()})` : ''}`}
                  name="price"
                  type="number"
                  value={formData.price}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AttachMoneyIcon />
                      </InputAdornment>
                    ),
                  }}
                  error={!!errors.price}
                  helperText={errors.price || "Optional: Enter the price for this treatment"}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Duration (minutes) *"
                  name="duration"
                  type="number"
                  value={formData.duration}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccessTimeIcon />
                      </InputAdornment>
                    ),
                  }}
                  error={!!errors.duration}
                  helperText={errors.duration}
                  required
                />
              </Grid>
              
              {/* Required Equipment */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Required Equipment
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', mb: 2 }}>
                  <TextField
                    fullWidth
                    label="Add Equipment"
                    value={newEquipment}
                    onChange={(e) => setNewEquipment(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddEquipment();
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAddEquipment}
                    startIcon={<AddIcon />}
                    sx={{ ml: 1 }}
                  >
                    Add
                  </Button>
                </Box>
                
                {formData.requiredEquipment.length > 0 ? (
                  <List>
                    {formData.requiredEquipment.map((item, index) => (
                      <ListItem key={index} divider={index < formData.requiredEquipment.length - 1}>
                        <ListItemText primary={item} />
                        <ListItemSecondaryAction>
                          <IconButton edge="end" onClick={() => handleRemoveEquipment(index)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No equipment added yet. Add equipment required for this treatment.
                  </Typography>
                )}
              </Grid>
              
              {/* Additional Notes */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Additional Notes
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  placeholder="Add any additional notes or instructions for this treatment"
                />
              </Grid>
              
              {/* Submit Button */}
              <Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/treatments')}
                  sx={{ mr: 2 }}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Treatment'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Box>

      {/* Tooth Selector Dialog */}
      <ToothSelector
        open={toothSelectorOpen}
        onClose={() => {
          setToothSelectorOpen(false);
          setCurrentProcedureTreatment(null);
        }}
        onConfirm={handleTeethSelected}
        treatment={currentProcedureTreatment}
        initialTeeth={currentProcedureTreatment ? 
          (selectedProcedures.find(p => 
            (p.treatment._id || p.treatment.id) === (currentProcedureTreatment._id || currentProcedureTreatment.id)
          )?.teeth || []) : []
        }
      />
    </Container>
  );
};

export default AddTreatment;